import React, { useEffect, useRef, useState } from "react";
import localeText from "../../../../locale";
import PhysicalStoreService from "../../../../service/PhysicalStoreService";
import { useToastContext } from "../../../../contexts/ToastContext/ToastContext";
import WebcamCapture from "./qrcode/WebcamCapture";
import jsQR from "jsqr";
import { Dialog } from "primereact/dialog";
import { useToggle } from "../../../../hooks/useToggle";
import { Button } from "primereact/button";
import { InputNumber } from "primereact/inputnumber";
import { formatPrice } from "../../../../utilities/formatPrice";
import { formatDate } from "../../../../utilities/formatDate";
import "./QrCodeDialog.css";

const QrCodePayDialog = ({ visible, onHide, user, currency }) => {
  const [transaction, setTransaction] = useState();
  const [isLoadingTransaction, setIsLoadingTransaction] = useState(true);
  const [isLoadingPaymentTransaction, setIsLoadingPaymentTransaction] = useState(false);
  const [amountWallet, setAmountWallet] = useState();
  const [amountWalletExtraCredit, setAmountWalletExtraCredit] = useState();
  const [actualPage, setActualPage] = useState(0);

  const { value: transactionModalVisible, setAsFalse, setAsTrue } = useToggle();

  const { showSuccessToast, showErrorToast } = useToastContext();

  const physicalStoreService = new PhysicalStoreService();

  const wasVisibleRef = useRef(false);

  const resetMainFlow = () => {
    setAsFalse();
    setTransaction(undefined);
    setActualPage(0);
    setAmountWallet(undefined);
    setAmountWalletExtraCredit(undefined);
    setIsLoadingTransaction(true);
    setIsLoadingPaymentTransaction(false);
  };

  useEffect(() => {
    if (wasVisibleRef.current && !visible) {
      resetMainFlow();
    }
    wasVisibleRef.current = visible;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reset only when visibility closes
  }, [visible]);

  const getTransaction = async (transactionId) => {
    try {
      setIsLoadingTransaction(true);

      setAsTrue();

      const res = await physicalStoreService.getPayment(transactionId);

      if (res.messageCode !== 200001) {
        showErrorToast({ code: res.messageCode });
        setAsFalse();
        return;
      }

      setTransaction(res.result);
    } catch (error) {
      console.error("Error fetching transaction:", error);
      setAsFalse();
    } finally {
      setIsLoadingTransaction(false);
    }
  };

  const handleScan = async (imageSrc) => {
    if (imageSrc) {
      const image = new Image();
      image.src = imageSrc;
      image.onload = async () => {
        const canvas = document.createElement("canvas");
        canvas.width = image.width;
        canvas.height = image.height;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(image, 0, 0, canvas.width, canvas.height);
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const code = jsQR(imageData.data, imageData.width, imageData.height, {
          inversionAttempts: "dontInvert",
        });
        if (code) {
          await getTransaction(code.data);
        }
      };
    }
  };

  const handlePay = async () => {
    try {
      setIsLoadingPaymentTransaction(true);

      if (
        (isNaN(amountWalletExtraCredit) ? 0 : amountWalletExtraCredit) +
          (isNaN(amountWallet) ? 0 : amountWallet) !==
        transaction.amountPaid
      ) {
        setIsLoadingPaymentTransaction(false);
        return showErrorToast({ detail: "sumOfValuesIsDifferent" });
      }

      const res = await physicalStoreService.confirmTransaction({
        amount: amountWallet ? amountWallet : 0,
        authentication: transaction.authentication,
        paymentid: transaction.id,
        amountWalletExtraCredit: amountWalletExtraCredit ? amountWalletExtraCredit : 0,
        buyer_username: user.username,
      });

      if (res.messageCode !== 200000) {
        showErrorToast({ code: res.messageCode });
        return;
      }

      showSuccessToast({ detail: "transactionDone" });
      setActualPage(1);
      closeTransactionModal();
    } catch (error) {
      console.error("Error confirming transaction:", error);
    } finally {
      setIsLoadingPaymentTransaction(false);
    }
  };

  const closeTransactionModal = () => {
    setAsFalse();
    setAmountWallet(undefined);
    setAmountWalletExtraCredit(undefined);
  };

  const changeToConfirmationPage = () => setActualPage(0);

  const handleMainDialogHide = () => {
    if (isLoadingPaymentTransaction) return;
    resetMainFlow();
    onHide?.();
  };

  return (
    <>
      <Dialog
        header={
          <div className="flex flex-column gap-2">
            <div className="text-lg font-medium" style={{ color: "var(--theme-text)" }}>
              {localeText("payWithQrCode") || "Paga con QR Code"}
            </div>
            <div className="text-sm font-normal" style={{ color: "var(--theme-text-muted)" }}>
              {localeText("payWithQrCodeDescription")}
            </div>
          </div>
        }
        visible={visible}
        className="w-full m-2 qr-code-pay-dialog"
        style={{ maxWidth: "min(720px, 95vw)" }}
        onHide={handleMainDialogHide}
        draggable={false}
        modal
        dismissableMask={!isLoadingPaymentTransaction}
        closable={!isLoadingPaymentTransaction}
      >
        {actualPage === 0 ? (
          <div
            className="flex flex-column align-items-center justify-content-center pt-2"
            style={{ maxHeight: "min(70vh, 720px)", maxWidth: "100%" }}
          >
            <WebcamCapture onScan={handleScan} />
          </div>
        ) : (
          <div className="flex flex-column gap-2">
            <h4 className="my-2 mt-0">{localeText("transactionDone")}</h4>

            <p className="mb-0">
              {localeText("idOfTransaction")}: {transaction.id}
            </p>
            <p className="mb-0">
              {localeText("value")}: {currency} {formatPrice(transaction?.amountPaid)}
            </p>
            <p className="mb-0">
              {localeText("to")}: {transaction?.seller?.firstname} {transaction?.seller?.surname} (
              {transaction?.seller?.username})
            </p>
            <p className="mb-0">
              {localeText("payer")}: {user?.firstname} {user?.surname} ({user?.username})
            </p>
            <p className="mb-0">
              {localeText("paymentDate")}: {formatDate(transaction?.dateTime)}
            </p>

            <Button
              label={localeText("makeAnotherPayment")}
              onClick={changeToConfirmationPage}
              icon="pi pi-dollar"
              className="mt-2"
            />
          </div>
        )}
      </Dialog>

      <Dialog
        header={localeText("transaction")}
        visible={transactionModalVisible}
        className="w-30rem m-2"
        onHide={() => {
          if (!isLoadingTransaction) closeTransactionModal();
        }}
        draggable={false}
        modal
      >
        {isLoadingTransaction ? (
          <div className="flex justify-content-center align-items-center">
            <i className="pi pi-spin pi-spinner text-600 text-3xl my-2"></i>
          </div>
        ) : (
          <div className="flex flex-column gap-2">
            <p className="mb-0">{localeText("paymentTransaction")}</p>
            <p className="mb-0">
              {localeText("user")}: {transaction.seller.firstname} {transaction?.seller?.surname} (
              {transaction.seller.username})
            </p>
            <p className="mb-0">
              {localeText("transactionValue2")}: {currency} {formatPrice(transaction.amountPaid)}
            </p>

            <div className="flex flex-column gap-2">
              <label className="font-bold">{localeText("payWithWallet")}</label>
              <InputNumber
                value={amountWallet}
                onChange={(e) => setAmountWallet(e.value)}
                min={0}
                mode="currency"
                currency={currency}
                placeholder={`${currency} 0,00`}
              />
            </div>

            <div className="flex flex-column gap-2">
              <label className="font-bold">{localeText("payWithExtracredit")}</label>

              <InputNumber
                value={amountWalletExtraCredit}
                onChange={(e) => setAmountWalletExtraCredit(e.value)}
                min={0}
                mode="currency"
                currency={currency}
                placeholder={`${currency} 0,00`}
              />
            </div>

            <div className="flex justify-content-end">
              <Button
                label={localeText("pay")}
                onClick={handlePay}
                loading={isLoadingPaymentTransaction}
                icon="pi pi-check"
              />
            </div>
          </div>
        )}
      </Dialog>
    </>
  );
};

export default QrCodePayDialog;
